'use client';

import copy from '@/locales/third-party/oneTrust.en.json';
import { IS_US } from '@/utils/env/helper';

type BannerMode = 'global' | 'custom' | 'unchanged';

// 当前 US OneTrust 域配置下的地区规则 ID，不根据 IP 或可变文案二次判断。
const RULE_MODES: Readonly<Record<string, BannerMode>> = {
  '0196332b-1e2b-7197-b54c-961f328c4906': 'global',
  '0196332b-1c61-7a43-a067-23991ef9719d': 'custom',
  '0196332b-1c61-7e5f-adef-1ec4c4c17665': 'custom',
};
const ATTRIBUTE = 'data-zl-onetrust-banner';
let activeCleanup: (() => void) | undefined;

export function getOneTrustBannerMode(
  resourceUrl: string,
  domainScript: string,
): BannerMode | null {
  try {
    const url = new URL(resourceUrl);
    const parts = url.pathname.split('/');
    if (
      url.origin !== 'https://cookie-cdn.cookiepro.com' ||
      parts.length !== 5 ||
      parts[1] !== 'consent' ||
      parts[2] !== domainScript ||
      !parts[4].endsWith('.json')
    ) {
      return null;
    }
    return RULE_MODES[parts[3]] ?? 'unchanged';
  } catch {
    return null;
  }
}

function replaceBannerCopy(policy: HTMLElement): void {
  const expectedText =
    copy.beforePrivacyPolicy +
    copy.privacyPolicy +
    copy.betweenLinks +
    copy.dataSubjectRequestForm +
    '.';
  if (
    policy.textContent === expectedText &&
    policy.querySelector('a[href="/privacy-security"]') &&
    policy.querySelector('a[href="/DSR"]')
  ) {
    return;
  }
  const privacyLink = document.createElement('a');
  privacyLink.href = '/privacy-security';
  privacyLink.textContent = copy.privacyPolicy;
  const dsrLink = document.createElement('a');
  dsrLink.href = '/DSR';
  dsrLink.textContent = copy.dataSubjectRequestForm;
  policy.replaceChildren(copy.beforePrivacyPolicy, privacyLink, copy.betweenLinks, dsrLink, '.');
}

/**
 * 在 SDK 加载前安装 US 展示覆盖。
 * SDK 未公开所选规则 ID，故读取它实际加载的模板资源 URL；不额外请求配置或修改授权。
 * 未识别到规则/加载失败时恢复原始 UI，避免永久隐藏同意入口。
 */
export function installOneTrustUsBanner(domainScript: string): () => void {
  if (!IS_US || typeof window === 'undefined') return () => undefined;
  if (activeCleanup) return activeCleanup;

  let mode: BannerMode | null = null;
  let root: HTMLElement | null = null;
  let resourceObserver: PerformanceObserver | undefined;
  let stopped = false;
  const html = document.documentElement;
  html.setAttribute(ATTRIBUTE, 'pending');

  const sync = () => {
    if (stopped || mode !== 'custom') return;
    const sdk = document.getElementById('onetrust-consent-sdk');
    if (sdk && sdk !== root) {
      root = sdk;
      domObserver.disconnect();
      domObserver.observe(sdk, { childList: true, subtree: true, characterData: true });
    }
    const policy = document.getElementById('onetrust-policy-text');
    if (policy) {
      replaceBannerCopy(policy);
      html.setAttribute(ATTRIBUTE, 'custom');
      clearTimeout(timeout);
    }
  };
  const domObserver = new MutationObserver(sync);
  // SDK 延迟插入；找到 SDK 根节点后仅观察该子树。
  domObserver.observe(html, { childList: true, subtree: true });

  const cleanup = () => {
    if (stopped) return;
    stopped = true;
    clearTimeout(timeout);
    domObserver.disconnect();
    resourceObserver?.disconnect();
    window.removeEventListener('OneTrustGroupsUpdated', sync);
    html.removeAttribute(ATTRIBUTE);
    activeCleanup = undefined;
  };
  activeCleanup = cleanup;
  const timeout = setTimeout(cleanup, 30000);
  window.addEventListener('OneTrustGroupsUpdated', sync);

  const inspect = (entries: PerformanceEntry[]) => {
    if (stopped) return;
    for (const entry of entries) {
      const nextMode = getOneTrustBannerMode(entry.name, domainScript);
      if (!nextMode) continue;
      mode = nextMode;
      if (mode === 'unchanged') {
        cleanup();
      } else if (mode === 'global') {
        html.setAttribute(ATTRIBUTE, 'global');
        clearTimeout(timeout);
        domObserver.disconnect();
      } else {
        sync();
      }
      resourceObserver?.disconnect();
      break;
    }
  };
  try {
    // Resource Timing 的跨域条目包含请求 URL；不需要访问远程响应内容。
    resourceObserver = new PerformanceObserver((list) => inspect(list.getEntries()));
    resourceObserver.observe({ type: 'resource', buffered: true });
    inspect(performance.getEntriesByType('resource'));
  } catch {
    cleanup();
  }
  return cleanup;
}
